import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNavBar from "@/components/BottomNavBar";
import { Button } from "@/components/ui/button";
import { Heart, Home, Sparkles, Globe, ArrowRight } from "lucide-react";

const USE_CASES = [
    { icon: Home, title: "Mom's house gets cleaned", body: "Book a deep clean for mom while you're abroad — pros arrive, scrub top to bottom, send you photos." },
    { icon: Sparkles, title: "Dad gets a driver for the day", body: "Doctor's appointments, grocery runs — a Royal driver on-call for your parents while you're overseas." },
    { icon: Heart, title: "Grandma gets groceries delivered", body: "A verified shopper picks up her list, delivers it. Pay in USD on your side, cash in JMD on hers." },
];

export default function SendHomePage() {
    return (
        <div className="min-h-screen bg-background pb-24" data-testid="send-home-page">
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0">
                    <img src="https://images.unsplash.com/photo-1555930762-4b24c58e4ba7?w=1920" alt="Jamaican sunset" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-emerald-900/40" />
                </div>
                <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 lg:py-36 text-center">
                    <Globe size={44} className="gold-text mx-auto mb-6" strokeWidth={1} />
                    <div className="overline mb-3">For the diaspora</div>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
                        Send help <span className="italic gold-grad">back home.</span>
                    </h1>
                    <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
                        You're in NY, Toronto, London — your family is in Mandeville, Mo Bay, Kingston. One tap and a verified Royal pro shows up at their door.
                    </p>
                    <div className="mt-10 flex gap-3 justify-center flex-wrap">
                        <Button asChild className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7" data-testid="send-home-giftcard-cta">
                            <Link to="/giftcards"><Heart size={14} className="mr-2" /> Send a gift card</Link>
                        </Button>
                        <Button asChild variant="outline" className="rounded-sm uppercase tracking-[0.2em] text-xs h-12 px-7 border-white/20" data-testid="send-home-browse-cta">
                            <Link to="/services">Book a service directly <ArrowRight size={14} className="ml-2" /></Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
                <div className="text-center mb-16">
                    <div className="overline mb-3">The Royal way</div>
                    <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter">
                        Love them from <span className="italic gold-grad">anywhere.</span>
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {USE_CASES.map((u, i) => (
                        <div key={`use-case-${u.title}`} className="bg-card border border-white/10 rounded-sm p-7 hover:border-primary/30 transition-colors" data-testid={`use-case-${i}`}>
                            <u.icon size={28} className="gold-text mb-5" strokeWidth={1.2} />
                            <h3 className="font-serif text-2xl mb-3">{u.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{u.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* How it works */}
            <section className="bg-card/40 border-y border-white/5">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
                    <div className="text-center mb-12">
                        <div className="overline mb-3">How it works</div>
                        <h2 className="font-serif text-3xl sm:text-4xl tracking-tighter">Three Royal steps.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { n: "01", t: "Pick a service or gift card", d: "Browse Royal services or buy a flexible gift card they can spend on anything." },
                            { n: "02", t: "Pay in your currency", d: "HandyPay charges your card in USD / CAD / GBP. Funds settle in JMD on our side." },
                            { n: "03", t: "They're served, you're free", d: "Pro arrives, does the job. We send you photos + confirmation. No distance. All love." },
                        ].map((s, i) => (
                            <div key={`step-${s.n}`} className="bg-background border border-white/10 rounded-sm p-6" data-testid={`step-${i}`}>
                                <div className="gold-text font-serif text-4xl mb-3">{s.n}</div>
                                <div className="font-serif text-xl mb-2">{s.t}</div>
                                <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
            <BottomNavBar />
        </div>
    );
}
